import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const displayValue = (value) => (value != null && String(value).trim() ? value : '—')

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join(' ')
}

const getClientName = (client) => {
    if (!client) return '—'
    if (typeof client === 'string') return client
    const name = [client.first_name, client.last_name].filter(Boolean).join(' ')
    if (name) return name
    if (client.company?.name) return client.company.name
    if (client.name) return client.name
    return '—'
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return Number.isNaN(date.getTime())
        ? dateStr
        : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatFrequency = (freq) => {
    if (!freq) return '—'
    return freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase()
}

/* ─── component ───────────────────────────────────────────────────────────── */

const RecurringInvoices = () => {
    const [profiles, setProfiles] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [expandedId, setExpandedId] = useState(null)

    const fetchProfiles = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.get('/recurring-invoices/')
            setProfiles(Array.isArray(response.data) ? response.data : [])
        } catch (err) {
            console.error('Failed to fetch recurring invoice profiles', err)
            setError(getErrorMessage(err, 'Failed to load recurring invoices. Please try again.'))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchProfiles()
    }, [fetchProfiles])

    const toggleDetails = (profileId) => {
        setExpandedId((current) => (current === profileId ? null : profileId))
    }

    const toggleActiveStatus = async (profile) => {
        try {
            const res = await api.patch(`/recurring-invoices/${profile.id}/`, {
                is_active: !profile.is_active
            })
            setProfiles(prev => prev.map(p => p.id === profile.id ? res.data : p))
        } catch (err) {
            console.error('Failed to toggle active status', err)
            alert('Failed to update recurring invoice status.')
        }
    }

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content content-two pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mb-3">
                        <h5 className="d-flex align-items-center mb-0">
                            Recurring Invoices
                            {!loading && !error && (
                                <span className="badge bg-soft-dark ms-2 text-dark fs-12">
                                    {profiles.length} {profiles.length === 1 ? 'Profile' : 'Profiles'}
                                </span>
                            )}
                        </h5>
                    </div>

                    <div className="card shadow-sm">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="fw-bold mb-0">All Recurring Invoices</h5>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-white"
                                onClick={fetchProfiles}
                                disabled={loading}
                            >
                                <i className="ti ti-refresh me-1" />
                                Refresh
                            </button>
                        </div>

                        <div className="card-body p-0">
                            {loading && (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <BeatLoader color="#3550DC" size={10} />
                                </div>
                            )}

                            {!loading && error && (
                                <div className="alert alert-danger m-3 mb-0" role="alert">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span>
                                            <i className="ti ti-alert-circle me-2" />
                                            {error}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={fetchProfiles}>
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!loading && !error && profiles.length === 0 && (
                                <div className="text-center py-5">
                                    <div className="avatar avatar-lg bg-light text-muted mx-auto mb-3">
                                        <i className="ti ti-file-invoice fs-24" />
                                    </div>
                                    <h6 className="fw-semibold mb-1">No recurring invoices found</h6>
                                    <p className="text-muted mb-0">Recurring invoice profiles will show up here once created.</p>
                                </div>
                            )}

                            {!loading && !error && profiles.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Client</th>
                                                <th>Frequency</th>
                                                <th>Next Run Date</th>
                                                <th>Currency</th>
                                                <th>Due Days</th>
                                                <th>Auto Send</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map((profile) => {
                                                const isExpanded = expandedId === profile.id

                                                return (
                                                    <React.Fragment key={profile.id}>
                                                        <tr>
                                                            <td>
                                                                <span className="fw-medium text-dark">
                                                                    {getClientName(profile.client_name)}
                                                                </span>
                                                            </td>
                                                            <td>{formatFrequency(profile.frequency)}</td>
                                                            <td>{formatDate(profile.next_run_date)}</td>
                                                            <td>{displayValue(profile.currency)}</td>
                                                            <td>{displayValue(profile.due_days)} Days</td>
                                                            <td>
                                                                <span className={`badge ${profile.auto_send ? 'badge-soft-success' : 'badge-soft-secondary'}`}>
                                                                    {profile.auto_send ? 'Yes' : 'No'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${profile.is_active ? 'badge-soft-success' : 'badge-soft-danger'}`}>
                                                                    {profile.is_active ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                <div className="d-inline-flex align-items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => toggleDetails(profile.id)}
                                                                        aria-expanded={isExpanded}
                                                                    >
                                                                        <i className={`ti ${isExpanded ? 'ti-chevron-up' : 'ti-chevron-down'} me-1`} />
                                                                        {isExpanded ? 'Hide' : 'Details'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`btn btn-sm ${profile.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                                        onClick={() => toggleActiveStatus(profile)}
                                                                    >
                                                                        {profile.is_active ? 'Pause' : 'Activate'}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {isExpanded && (
                                                            <tr className="bg-light">
                                                                <td colSpan={8} className="p-0">
                                                                    <div className="p-3 px-4 border-top">
                                                                        <div className="row g-3">
                                                                            <div className="col-md-6">
                                                                                <p className="text-muted fs-12 mb-1">Payment Instructions</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(profile.payment_instructions)}</p>
                                                                            </div>
                                                                            <div className="col-md-6">
                                                                                <p className="text-muted fs-12 mb-1">Notes</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(profile.notes)}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RecurringInvoices